import * as React from 'react';
import { Button, Input, Modal, Select, TextBox } from '../..';
import { useAuth } from '../../../context/authContext';
import {
  Category,
  storePreferenceFormAction,
} from '../../../context/storeContext';
import useValidator from '../../../hooks/useValidator';
import { Option } from '../../Inputs/SelectInput/SelectInput';

type ModalProps = {
  onCloseModal: () => void;
  onSetAddedValue: (option: Option) => void;
  categories: Category[];
};

function CategoryAdd({
  onCloseModal,
  onSetAddedValue,
  categories,
}: ModalProps) {
  const [{ user, storePreferences }, dispatch] = useAuth();
  const [category, setCategory] = React.useState({
    name: '',
    description: '',
    parentId: '',
    icon: '',
  });
  const [status, setStatus] = React.useState<
    'IDLE' | 'LOADING' | 'RESOLVED' | 'REJECTED'
  >('IDLE');
  const [validator, showValidationMessage] = useValidator({});

  const handleCategoryInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;

    setCategory(fndCategory => ({
      ...fndCategory,
      [name]: value,
    }));
  };

  const handleCategorySave = async () => {
    if (!validator.allValid()) {
      showValidationMessage(true);
      return;
    }

    setStatus('LOADING');

    const storeId = user?.defaultStore?.id;

    const { category: getAddedCategory } = (await storePreferenceFormAction(
      `/category?store=${storeId}`,
      'post',
      { ...category, storeId },
    )) as any;

    if (storePreferences && getAddedCategory) {
      dispatch({
        type: 'SET_STORE_PREFERENCES',
        payload: {
          ...storePreferences,
          categories:
            storePreferences.categories.length > 0
              ? [getAddedCategory, ...storePreferences.categories]
              : [getAddedCategory],
        },
      });
    }

    onSetAddedValue({
      value: getAddedCategory.id,
      label: getAddedCategory.name,
    });

    setStatus('RESOLVED');
    onCloseModal();
  };

  return (
    <Modal onClose={onCloseModal} show>
      <div className="rounded-md bg-white tablet:w-[500px]">
        <form>
          <div className="flex flex-col p-5">
            <h3
              className="text-pos-black text-[20px] font-semibold mb-2
              inline-flex whitespace-pre"
            >
              Add New Category
            </h3>
            <div className="text-pos-black text-sm mt-2 mb-2 font-normal space-y-4">
              <div>
                <label htmlFor="name" className="text-xbase block mb-1">
                  Name{' '}
                  <span className="text-xs font-light text-pos-red">*</span>
                </label>
                <Input
                  id="name"
                  name="name"
                  value={category.name}
                  onChange={handleCategoryInputChange}
                />
                {validator.message('name', category.name, 'required|min:3', {
                  messages: {
                    required: 'Category name is required.',
                  },
                  className:
                    'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                })}
              </div>
              <div>
                <label htmlFor="description" className="text-xbase block mb-1">
                  Description
                </label>
                <TextBox
                  id="description"
                  name="description"
                  value={category.description}
                  rows={3}
                  onChange={handleCategoryInputChange}
                />
              </div>
              <div className="w-full !mt-[0.65rem]">
                <label htmlFor="parentId" className="text-xbase block mb-1">
                  Parent Category
                </label>
                <Select
                  placeholder={{
                    value: '',
                    label: 'Assign a parent category',
                  }}
                  value={category.parentId}
                  options={
                    categories.length > 0
                      ? categories.map(fndCategory => ({
                          value: fndCategory.id,
                          label: fndCategory.name,
                        }))
                      : []
                  }
                  name="parentId"
                  id="parentId"
                  onChange={handleCategoryInputChange}
                  className="w-full"
                />
                <span className="text-pos-gray font-light text-sm mt-2 inline-block leading-normal">
                  Assign a parent category to create a hierarchy. The category
                  Drink, for example, would be the parent of Fruit Juice.
                </span>
              </div>
            </div>
            <div className="mt-4 text-center flex flex-row ml-auto space-x-4">
              <Button
                label="Save"
                onClick={handleCategorySave}
                type="button"
                disabled={status === 'LOADING'}
                loading={status === 'LOADING'}
                className="!bg-pos-green border border-[rgba(27,31,36,0.15)] min-w-[150px]  h-[40px]
                      items-center justify-center text-white text-xbase
                      shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)] shrink-0 font-medium"
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default CategoryAdd;
